import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faTrashCan,
  faXmark,
  faCircleCheck,
  faTriangleExclamation,
  faRotate,
  faImage,
  faUpload,
  faArrowUpRightFromSquare,
  faPersonChalkboard,
  faFileLines,
  faClipboardList,
  faLayerGroup,
  faChalkboardUser,
  faNoteSticky,
} from '@fortawesome/free-solid-svg-icons';
import { DEMO_COURSES } from '../data/courses';
import './Curriculum.css';

const API = process.env.REACT_APP_API_BASE_URL;
const URLS = {
  demo: `${API}/api/admin/Curriculum/demo`,
  modules: `${API}/api/admin/Curriculum/modules`,
  courses: `${API}/api/admin/Curriculum/courses`,
};
// the old page sent no token; it's added so the server CAN check it (it changes nothing if it doesn't)
const auth = (extra = {}) => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}`, ...extra } });

const PLANS = {
  1: { key: 'little', label: 'Little', grades: 'Grade 1 – Grade 3' },
  2: { key: 'junior', label: 'Junior', grades: 'Grade 4 – Grade 12' },
};
const TABS = [
  { key: 'demo', label: 'Demo sessions', icon: faChalkboardUser, sub: 'Free trial projects' },
  { key: 'little', label: 'Little course', icon: faLayerGroup, plan: 1 },
  { key: 'junior', label: 'Junior course', icon: faLayerGroup, plan: 2 },
];
const MAX_IMAGE = 5 * 1024 * 1024;

const EMPTY_FORM = {
  name: '',
  order: '',
  details: '',
  link: '',
  image: null,
  imagePreview: '',
  ageRange: '',
  gradeRange: '',
  plan: 1,
  forCourse: '',
  note: '',
  assignment: '',
  ppt: '',
};

const errText = (err, fallback) => {
  const data = err?.response?.data;
  if (data && typeof data === 'object' && (data.error || data.message)) return data.error || data.message;
  const status = err?.response?.status;
  if (status) return `${fallback} (server error ${status})`;
  return `${fallback} (no answer from the server: check the internet or the server)`;
};
const byOrder = (a, b) => (Number(a.order) || 999) - (Number(b.order) || 999);

/* Image with an icon when missing or broken (the old placeholder site no longer works) */
const Thumb = ({ src, alt, className }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) {
    return (
      <span className={`${className} rmacu-noimg`} aria-hidden="true">
        <FontAwesomeIcon icon={faImage} />
      </span>
    );
  }
  return <img className={className} src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />;
};

/* Error box with a retry button */
const Retry = ({ text, onRetry }) => (
  <div className="rmacu-empty">
    <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
    <p>{text}</p>
    <button type="button" className="rmacu-btn" onClick={onRetry}>
      <FontAwesomeIcon icon={faRotate} /> Try again
    </button>
  </div>
);

const Curriculum = () => {
  const [params, setParams] = useSearchParams();
  const tabKey = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'demo';
  const tab = TABS.find((t) => t.key === tabKey);
  const plan = tab.plan; // 1 / 2 / undefined (demo)

  // ---------- Data ----------
  const [demos, setDemos] = useState({ 1: [], 2: [] });
  const [modules, setModules] = useState({ 1: [], 2: [] });
  const [activeModule, setActiveModule] = useState({ 1: null, 2: null });
  const [courses, setCourses] = useState({ 1: [], 2: [] });
  const [loading, setLoading] = useState({ demo: false, modules: false, courses: false });
  const [loadError, setLoadError] = useState({ demo: '', modules: '', courses: '' });
  const [reload, setReload] = useState(0);
  const [courseReload, setCourseReload] = useState(0);

  // ---------- Windows ----------
  const [modalType, setModalType] = useState(''); // addDemo | editDemo | addModule | editModule | addCourse | editCourse
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [toDelete, setToDelete] = useState(null); // { type, item }
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [toast, setToast] = useState('');

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'demo') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  // ---------- Load demo sessions (same endpoint, same mapping) ----------
  const fetchDemoSessions = useCallback(async () => {
    setLoading((p) => ({ ...p, demo: true }));
    setLoadError((p) => ({ ...p, demo: '' }));
    try {
      const response = await axios.get(URLS.demo, auth());
      const map = (item) => ({
        id: item.Id,
        name: item.ProjectName,
        image: item.ImgLink,
        details: item.ProjectDetails,
        link: item.ProjectLink,
        ageRange: item.AgeDetails,
        gradeRange: item.GradeDetails,
        plan: item.Plan,
        forCourse: item.ForCourse || '',
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      setDemos({
        1: rows.filter((i) => Number(i.Plan) === 1).map(map),
        2: rows.filter((i) => Number(i.Plan) === 2).map(map),
      });
    } catch (err) {
      console.error('Error fetching demo sessions:', err);
      setLoadError((p) => ({ ...p, demo: 'We couldn’t load the demo sessions.' }));
    } finally {
      setLoading((p) => ({ ...p, demo: false }));
    }
  }, []);

  // ---------- Load modules of a plan (same endpoint) ----------
  const fetchModules = useCallback(async (pl) => {
    setLoading((p) => ({ ...p, modules: true }));
    setLoadError((p) => ({ ...p, modules: '' }));
    try {
      const response = await axios.get(`${URLS.modules}/${pl}`, auth());
      const list = (Array.isArray(response.data) ? response.data : [])
        .map((m) => ({ id: m.Id, name: m.ModuleName, order: m.Order, plan: pl }))
        .sort(byOrder);
      setModules((p) => ({ ...p, [pl]: list }));
      // keep the module that was open (the old page always jumped back to the first one)
      setActiveModule((p) => {
        const still = list.some((m) => String(m.id) === String(p[pl]));
        return { ...p, [pl]: still ? p[pl] : list[0]?.id ?? null };
      });
    } catch (err) {
      console.error('Error fetching modules:', err);
      setLoadError((p) => ({ ...p, modules: 'We couldn’t load the modules.' }));
    } finally {
      setLoading((p) => ({ ...p, modules: false }));
    }
  }, []);

  useEffect(() => {
    if (plan) fetchModules(plan);
    else fetchDemoSessions();
  }, [plan, fetchModules, fetchDemoSessions, reload]);

  // ---------- Load courses of the open module (same endpoint, same mapping) ----------
  const moduleId = plan ? activeModule[plan] : null;
  useEffect(() => {
    if (!plan || !moduleId) return undefined;
    let cancelled = false;
    setLoading((p) => ({ ...p, courses: true }));
    setLoadError((p) => ({ ...p, courses: '' }));
    (async () => {
      try {
        const response = await axios.get(`${API}/api/admin/Curriculum/paid/${moduleId}/${plan}`, auth());
        if (cancelled) return;
        setCourses((p) => ({
          ...p,
          [plan]: (response.data?.courses || []).map((c) => ({
            id: c.Id,
            name: c.CourseName,
            image: c.CourseFilesLink,
            link: c.CourseLink,
            ppt: c.CourseProjectLink,
            note: c.CourseNote,
            assignment: c.CourseAssignmentLink,
            moduleId: c.IdModule,
          })),
        }));
      } catch (err) {
        console.error('Error fetching courses:', err);
        if (!cancelled) setLoadError((p) => ({ ...p, courses: 'We couldn’t load the courses of this module.' }));
      } finally {
        if (!cancelled) setLoading((p) => ({ ...p, courses: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plan, moduleId, courseReload]);

  // ---------- Small effects ----------
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // no page scroll behind an open window
  const anyOpen = Boolean(modalType || toDelete);
  useEffect(() => {
    if (!anyOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [anyOpen]);

  // Escape closes the top window
  useEffect(() => {
    if (!anyOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (toDelete) {
        if (!deleteBusy) setToDelete(null);
      } else if (!saving) setModalType('');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [anyOpen, toDelete, saving, deleteBusy]);

  // free the preview of a picked image
  useEffect(() => {
    const url = formData.imagePreview;
    return () => {
      if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [formData.imagePreview]);

  // ---------- Form ----------
  const openModal = (type, item = null) => {
    setModalType(type);
    setCurrentItem(item);
    setFormError('');
    if (type === 'editDemo') {
      setFormData({
        ...EMPTY_FORM,
        name: item.name || '',
        details: item.details || '',
        link: item.link || '',
        imagePreview: item.image || '',
        ageRange: item.ageRange || '',
        gradeRange: item.gradeRange || '',
        plan: item.plan,
        forCourse: item.forCourse || '',
      });
    } else if (type === 'editCourse') {
      setFormData({
        ...EMPTY_FORM,
        name: item.name || '',
        link: item.link || '',
        imagePreview: item.image || '',
        plan,
        note: item.note || '',
        assignment: item.assignment || '',
        ppt: item.ppt || '',
      });
    } else if (type === 'editModule') {
      setFormData({ ...EMPTY_FORM, name: item.name || '', order: item.order ?? '', plan: '' });
    } else {
      // add: demo picks the section it was opened from, courses follow the open tab
      setFormData({ ...EMPTY_FORM, plan: type === 'addModule' ? '' : item?.plan || plan || 1 });
    }
  };

  const closeModal = () => {
    if (saving) return;
    setModalType('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE) {
      setFormError('The image must be smaller than 5 MB.');
      return;
    }
    setFormError('');
    setFormData((prev) => ({ ...prev, image: file, imagePreview: URL.createObjectURL(file) }));
  };

  const isDemo = modalType.includes('Demo');
  const isModule = modalType.includes('Module');
  const isCourse = modalType.includes('Course');
  const isAdd = modalType.startsWith('add');

  const validateForm = () => {
    if (!formData.name.trim()) return 'Please enter a name.';
    if (isDemo) {
      if (!String(formData.details).trim()) return 'Please write the details.';
      if (!String(formData.ageRange).trim() || !String(formData.gradeRange).trim() || !formData.plan) return 'Age range, grade range and plan are required.';
    }
    if (isModule && formData.order !== '' && formData.order !== null && Number.isNaN(parseInt(formData.order, 10))) return 'Order must be a number.';
    return '';
  };

  // same endpoints + same payloads as before
  const handleSubmit = async (e) => {
    e.preventDefault();
    const problem = validateForm();
    if (problem) {
      setFormError(problem);
      return;
    }
    setSaving(true);
    setFormError('');
    const multipart = auth({ 'Content-Type': 'multipart/form-data' });
    try {
      if (modalType === 'addDemo' || modalType === 'editDemo') {
        const fd = new FormData();
        if (modalType === 'editDemo') fd.append('id', currentItem.id);
        fd.append('name', formData.name);
        fd.append('details', formData.details);
        fd.append('link', formData.link);
        fd.append('ageRange', formData.ageRange);
        fd.append('gradeRange', formData.gradeRange);
        fd.append('plan', formData.plan);
        fd.append('forCourse', formData.forCourse || '');
        if (modalType === 'editDemo') fd.append('existingImage', currentItem.image);
        if (formData.image) fd.append('image', formData.image);
        if (modalType === 'addDemo') await axios.post(URLS.demo, fd, multipart);
        else await axios.put(URLS.demo, fd, multipart);
        await fetchDemoSessions();
      } else if (modalType === 'addModule') {
        await axios.post(URLS.modules, { name: formData.name, order: formData.order || null, plan }, auth());
        await fetchModules(plan);
      } else if (modalType === 'editModule') {
        await axios.put(URLS.modules, { id: currentItem.id, name: formData.name, order: formData.order || null }, auth());
        await fetchModules(plan);
      } else if (modalType === 'addCourse' || modalType === 'editCourse') {
        const fd = new FormData();
        if (modalType === 'editCourse') fd.append('id', currentItem.id);
        fd.append('name', formData.name);
        fd.append('link', formData.link);
        fd.append('note', formData.note || '');
        fd.append('assignment', formData.assignment || '');
        fd.append('ppt', formData.ppt || '');
        if (modalType === 'editCourse') fd.append('existingImage', currentItem.image);
        if (formData.image) fd.append('image', formData.image);
        if (modalType === 'addCourse') {
          fd.append('moduleId', moduleId);
          fd.append('plan', formData.plan);
          await axios.post(URLS.courses, fd, multipart);
        } else {
          await axios.put(URLS.courses, fd, multipart);
        }
        setCourseReload((n) => n + 1);
      }
      const what = isDemo ? 'Demo session' : isModule ? 'Module' : 'Course';
      setToast(`${what} ${isAdd ? 'added' : 'saved'}`);
      setModalType('');
    } catch (err) {
      console.error('Error performing operation:', err);
      setFormError(errText(err, `Couldn’t ${isAdd ? 'add' : 'save'}. Please try again.`));
    } finally {
      setSaving(false);
    }
  };

  // ---------- Delete (same endpoints: DELETE with { id }) ----------
  const confirmDelete = async () => {
    const { type, item } = toDelete;
    setDeleteBusy(true);
    setDeleteError('');
    try {
      const url = type === 'demo' ? URLS.demo : type === 'module' ? URLS.modules : URLS.courses;
      await axios.delete(url, { ...auth(), data: { id: item.id } });
      if (type === 'demo') await fetchDemoSessions();
      else if (type === 'module') await fetchModules(plan);
      else setCourseReload((n) => n + 1);
      setToDelete(null);
      setToast(`${type === 'demo' ? 'Demo session' : type === 'module' ? 'Module' : 'Course'} deleted`);
    } catch (err) {
      console.error('Error deleting item:', err);
      setDeleteError(errText(err, 'Couldn’t delete. Please try again.'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const askDelete = (type, item) => {
    setDeleteError('');
    setToDelete({ type, item });
  };

  // ---------- Pieces ----------
  const renderDemoSection = (pl) => {
    const list = demos[pl];
    return (
      <section key={pl} className="rmacu-block">
        <div className="rmacu-block-head">
          <div>
            <h2>
              {PLANS[pl].label} <span>{PLANS[pl].grades}</span>
            </h2>
            <p>{list.length} demo {list.length === 1 ? 'session' : 'sessions'}</p>
          </div>
          <button type="button" className="rmacu-btn rmacu-btn--ghost" onClick={() => openModal('addDemo', { plan: pl })}>
            <FontAwesomeIcon icon={faPlus} /> Add demo
          </button>
        </div>
        {list.length === 0 ? (
          <p className="rmacu-none">No demo sessions for {PLANS[pl].label} yet.</p>
        ) : (
          <ul className="rmacu-cards">
            {list.map((demo) => (
              <li key={demo.id} className="rmacu-card">
                <Thumb src={demo.image} alt={demo.name} className="rmacu-card-img" />
                <div className="rmacu-card-body">
                  <h3>{demo.name}</h3>
                  <div className="rmacu-tags">
                    {demo.gradeRange && <span>{demo.gradeRange}</span>}
                    {demo.ageRange && <span>{demo.ageRange}</span>}
                    {demo.forCourse && <span className="is-course">{demo.forCourse}</span>}
                  </div>
                  {demo.details && <p className="rmacu-card-text">{demo.details}</p>}
                </div>
                <div className="rmacu-card-foot">
                  {demo.link ? (
                    <a className="rmacu-link" href={demo.link} target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> View demo
                    </a>
                  ) : (
                    <span className="rmacu-link is-missing">No link</span>
                  )}
                  <span className="rmacu-icons">
                    <button type="button" onClick={() => openModal('editDemo', demo)} aria-label={`Edit ${demo.name}`}>
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button type="button" className="is-danger" onClick={() => askDelete('demo', demo)} aria-label={`Delete ${demo.name}`}>
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  };

  const renderCourses = () => {
    const mods = modules[plan];
    const current = mods.find((m) => String(m.id) === String(moduleId));
    const list = courses[plan];

    if (loading.modules && mods.length === 0) {
      return (
        <div className="rmacu-split" aria-busy="true">
          <div className="rmacu-skel rmacu-skel--side" />
          <div className="rmacu-skel rmacu-skel--main" />
        </div>
      );
    }
    if (loadError.modules) return <Retry text={loadError.modules} onRetry={() => setReload((n) => n + 1)} />;

    return (
      <div className="rmacu-split">
        {/* modules */}
        <aside className="rmacu-modules" aria-label="Modules">
          <div className="rmacu-modules-head">
            <h2>Modules</h2>
            <button type="button" className="rmacu-mini" onClick={() => openModal('addModule')}>
              <FontAwesomeIcon icon={faPlus} /> Add
            </button>
          </div>
          {mods.length === 0 ? (
            <p className="rmacu-none">No modules yet. Add the first one.</p>
          ) : (
            <ul>
              {mods.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className={String(m.id) === String(moduleId) ? 'is-on' : ''}
                    onClick={() => setActiveModule((p) => ({ ...p, [plan]: m.id }))}
                    aria-current={String(m.id) === String(moduleId) ? 'true' : undefined}
                  >
                    {m.order ? <b>{m.order}</b> : <b className="is-blank">–</b>}
                    <span>{m.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* courses of the open module */}
        <section className="rmacu-main">
          {!current ? (
            <p className="rmacu-none">{mods.length ? 'Choose a module.' : 'Add a module to start adding courses.'}</p>
          ) : (
            <>
              <div className="rmacu-block-head">
                <div>
                  <h2>{current.name}</h2>
                  <p>
                    {current.order ? `Order ${current.order} · ` : ''}
                    {loading.courses ? 'Loading…' : `${list.length} ${list.length === 1 ? 'course' : 'courses'}`}
                  </p>
                </div>
                <div className="rmacu-head-actions">
                  <button type="button" className="rmacu-icon" onClick={() => openModal('editModule', current)} aria-label="Edit module">
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button type="button" className="rmacu-icon is-danger" onClick={() => askDelete('module', current)} aria-label="Delete module">
                    <FontAwesomeIcon icon={faTrashCan} />
                  </button>
                  <button type="button" className="rmacu-btn" onClick={() => openModal('addCourse')}>
                    <FontAwesomeIcon icon={faPlus} /> Add course
                  </button>
                </div>
              </div>

              {loading.courses ? (
                <div aria-busy="true">
                  <div className="rmacu-skel" />
                  <div className="rmacu-skel" />
                  <div className="rmacu-skel" />
                </div>
              ) : loadError.courses ? (
                <Retry text={loadError.courses} onRetry={() => setCourseReload((n) => n + 1)} />
              ) : list.length === 0 ? (
                <p className="rmacu-none">No courses in this module yet.</p>
              ) : (
                <ol className="rmacu-courses">
                  {list.map((c, index) => (
                    <li key={c.id} className="rmacu-course">
                      <span className="rmacu-num">{index + 1}</span>
                      <Thumb src={c.image} alt={c.name} className="rmacu-course-img" />
                      <div className="rmacu-course-body">
                        <h3>{c.name}</h3>
                        <div className="rmacu-links">
                          {c.link && (
                            <a href={c.link} target="_blank" rel="noopener noreferrer" className="is-project">
                              <FontAwesomeIcon icon={faPersonChalkboard} /> Project
                            </a>
                          )}
                          {c.ppt && (
                            <a href={c.ppt} target="_blank" rel="noopener noreferrer" className="is-ppt">
                              <FontAwesomeIcon icon={faFileLines} /> Course (PPT)
                            </a>
                          )}
                          {c.note && (
                            <a href={c.note} target="_blank" rel="noopener noreferrer" className="is-note">
                              <FontAwesomeIcon icon={faNoteSticky} /> Notes
                            </a>
                          )}
                          {c.assignment && (
                            <a href={c.assignment} target="_blank" rel="noopener noreferrer" className="is-task">
                              <FontAwesomeIcon icon={faClipboardList} /> Assignment
                            </a>
                          )}
                          {!c.link && !c.ppt && !c.note && !c.assignment && <span className="rmacu-dim">No links yet</span>}
                        </div>
                      </div>
                      <span className="rmacu-icons">
                        <button type="button" onClick={() => openModal('editCourse', c)} aria-label={`Edit ${c.name}`}>
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button type="button" className="is-danger" onClick={() => askDelete('course', c)} aria-label={`Delete ${c.name}`}>
                          <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </section>
      </div>
    );
  };

  // ---------- Page ----------
  const title = isDemo ? 'demo session' : isModule ? 'module' : 'course';
  const deleteCount = toDelete?.type === 'module' && String(toDelete.item.id) === String(moduleId) ? courses[plan]?.length || 0 : 0;

  return (
    <div className="rmacu">
      <header className="rmacu-header">
        <h1>Curriculum</h1>
        <p>Demo sessions, modules and courses.</p>
      </header>

      <div className="rmacu-tabs" role="tablist" aria-label="Curriculum">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tabKey === t.key} className={tabKey === t.key ? 'is-on' : ''} onClick={() => setTab(t.key)}>
            <FontAwesomeIcon icon={t.icon} />
            <span>{t.label}</span>
            <small>{t.plan ? PLANS[t.plan].grades : t.sub}</small>
          </button>
        ))}
      </div>

      {tabKey === 'demo' ? (
        loading.demo && demos[1].length + demos[2].length === 0 ? (
          <div aria-busy="true">
            <div className="rmacu-skel rmacu-skel--bar" />
            <div className="rmacu-cards">
              <div className="rmacu-skel rmacu-skel--card" />
              <div className="rmacu-skel rmacu-skel--card" />
              <div className="rmacu-skel rmacu-skel--card" />
            </div>
          </div>
        ) : loadError.demo ? (
          <Retry text={loadError.demo} onRetry={() => setReload((n) => n + 1)} />
        ) : (
          <>
            {renderDemoSection(1)}
            {renderDemoSection(2)}
          </>
        )
      ) : (
        renderCourses()
      )}

      {/* ---------- Add / edit ---------- */}
      {modalType && (
        <div className="rmacu-overlay" onMouseDown={(e) => e.target === e.currentTarget && closeModal()}>
          <form className="rmacu-modal" role="dialog" aria-modal="true" aria-labelledby="rmacu-form-title" onSubmit={handleSubmit}>
            <div className="rmacu-modal-head">
              <div>
                <h2 id="rmacu-form-title">
                  {isAdd ? 'Add' : 'Edit'} {title}
                </h2>
                <p>
                  {isDemo
                    ? `${PLANS[formData.plan]?.label || ''} demo`
                    : isModule
                    ? `${PLANS[plan].label} course`
                    : `${PLANS[plan].label} · ${modules[plan].find((m) => String(m.id) === String(moduleId))?.name || ''}`}
                </p>
              </div>
              <button type="button" className="rmacu-x" onClick={closeModal} disabled={saving} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="rmacu-modal-body">
              <div className={isModule ? 'rmacu-two rmacu-two--module' : ''}>
                <label className="rmacu-field">
                  <span>Name *</span>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder={isModule ? 'e.g. Scratch Games' : 'Name'} autoFocus />
                </label>
                {isModule && (
                  <label className="rmacu-field">
                    <span>Order</span>
                    <input type="number" name="order" min="1" value={formData.order} onChange={handleInputChange} placeholder="10, 20…" />
                  </label>
                )}
              </div>
              {isModule && <small className="rmacu-hint">Lower numbers come first. Leave the order empty to put it at the end.</small>}

              {isDemo && (
                <>
                  <label className="rmacu-field">
                    <span>Details *</span>
                    <textarea name="details" rows={4} value={formData.details} onChange={handleInputChange} placeholder="What the child builds in the demo" />
                  </label>
                  <label className="rmacu-field">
                    <span>Link</span>
                    <input type="url" name="link" value={formData.link} onChange={handleInputChange} placeholder="https://…" />
                  </label>
                  <label className="rmacu-field">
                    <span>For course</span>
                    <select name="forCourse" value={formData.forCourse} onChange={handleInputChange}>
                      <option value="">General demo (any course)</option>
                      {DEMO_COURSES.map((c) => (
                        <option key={c.title} value={c.title}>
                          {c.title}
                        </option>
                      ))}
                    </select>
                    <small className="rmacu-hint">When a parent asks for this course on the free class form, the teacher sees this demo suggested.</small>
                  </label>
                  <div className="rmacu-two">
                    <label className="rmacu-field">
                      <span>Age range *</span>
                      <input type="text" name="ageRange" value={formData.ageRange} onChange={handleInputChange} placeholder="e.g. 6 to 8 Years" />
                    </label>
                    <label className="rmacu-field">
                      <span>Grade range *</span>
                      <input type="text" name="gradeRange" value={formData.gradeRange} onChange={handleInputChange} placeholder="e.g. Grade 1 - Grade 3" />
                    </label>
                  </div>
                  <div className="rmacu-field">
                    <span>Plan *</span>
                    <div className="rmacu-seg" role="radiogroup" aria-label="Plan">
                      {[1, 2].map((p) => (
                        <button key={p} type="button" role="radio" aria-checked={Number(formData.plan) === p} className={Number(formData.plan) === p ? 'is-on' : ''} onClick={() => setFormData((f) => ({ ...f, plan: p }))}>
                          {PLANS[p].label} <small>{PLANS[p].grades}</small>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {isCourse && (
                <>
                  <div className="rmacu-two">
                    <label className="rmacu-field">
                      <span>Project link</span>
                      <input type="url" name="link" value={formData.link} onChange={handleInputChange} placeholder="https://…" />
                    </label>
                    <label className="rmacu-field">
                      <span>Course (PPT) link</span>
                      <input type="url" name="ppt" value={formData.ppt} onChange={handleInputChange} placeholder="https://…" />
                    </label>
                  </div>
                  <div className="rmacu-two">
                    <label className="rmacu-field">
                      <span>Notes link</span>
                      <input type="url" name="note" value={formData.note} onChange={handleInputChange} placeholder="https://…" />
                    </label>
                    <label className="rmacu-field">
                      <span>Assignment link</span>
                      <input type="url" name="assignment" value={formData.assignment} onChange={handleInputChange} placeholder="https://…" />
                    </label>
                  </div>
                </>
              )}

              {(isDemo || isCourse) && (
                <div className="rmacu-field">
                  <span>Image {isDemo ? '(optional)' : ''}</span>
                  <div className="rmacu-imgpick">
                    {formData.imagePreview ? (
                      <img src={formData.imagePreview} alt="Preview" />
                    ) : (
                      <span className="rmacu-noimg">
                        <FontAwesomeIcon icon={faImage} />
                      </span>
                    )}
                    <label className="rmacu-btn rmacu-btn--ghost">
                      <input type="file" accept="image/*" onChange={handleImageChange} />
                      <FontAwesomeIcon icon={faUpload} /> {formData.imagePreview ? 'Change image' : 'Choose image'}
                    </label>
                    <small>
                      {formData.image ? formData.image.name : !isAdd && formData.imagePreview ? 'Current image — kept unless you choose a new one.' : 'JPG, PNG or GIF, up to 5 MB.'}
                    </small>
                  </div>
                </div>
              )}

              {formError && (
                <p className="rmacu-error" role="alert">
                  {formError}
                </p>
              )}
            </div>

            <div className="rmacu-modal-foot">
              <button type="button" className="rmacu-btn rmacu-btn--ghost" onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="rmacu-btn" disabled={saving}>
                {saving ? (isAdd ? 'Adding…' : 'Saving…') : isAdd ? `Add ${title}` : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ---------- Delete confirm ---------- */}
      {toDelete && (
        <div className="rmacu-overlay" onMouseDown={(e) => e.target === e.currentTarget && !deleteBusy && setToDelete(null)}>
          <div className="rmacu-dialog" role="alertdialog" aria-modal="true" aria-labelledby="rmacu-del-title">
            <span className="rmacu-dialog-icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </span>
            <h2 id="rmacu-del-title">
              Delete this {toDelete.type === 'demo' ? 'demo session' : toDelete.type}?
            </h2>
            <p>
              <strong>{toDelete.item.name}</strong>
              {deleteCount > 0 && (
                <>
                  <br />
                  This module has {deleteCount} {deleteCount === 1 ? 'course' : 'courses'}.
                </>
              )}
              <br />
              This can’t be undone.
            </p>
            {deleteError && (
              <p className="rmacu-error" role="alert">
                {deleteError}
              </p>
            )}
            <div className="rmacu-dialog-actions">
              <button type="button" className="rmacu-btn rmacu-btn--ghost" onClick={() => setToDelete(null)} disabled={deleteBusy}>
                Cancel
              </button>
              <button type="button" className="rmacu-btn rmacu-btn--danger" onClick={confirmDelete} disabled={deleteBusy}>
                {deleteBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="rmacu-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default Curriculum;